/**
 * help-panel.tsx
 *
 * Slide-out help & feedback panel. Triggered by the ? icon in the sidebar.
 * Two tabs:
 *   Help   — searchable articles covering all features
 *   Feedback — bug/feature/question form → GitHub Issue in cushty-feedback repo
 */
import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Search, ChevronRight, ChevronLeft, CheckCircle2, Loader2, HelpCircle,
} from "lucide-react";
import {
  HELP_ARTICLES, HELP_CATEGORIES,
  type HelpArticle,
} from "@/lib/help-articles";

const APP_VERSION = "1.0.0";

interface Props {
  open: boolean;
  onClose: () => void;
  role?: "admin" | "engineer";
}

// ── Article detail view ──────────────────────────────────────────────────────
function ArticleView({ article, onBack }: { article: HelpArticle; onBack: () => void }) {
  return (
    <div className="flex flex-col h-full">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4 transition-colors"
        data-testid="button-help-back"
      >
        <ChevronLeft className="w-3 h-3" /> Back
      </button>
      <Badge variant="secondary" className="w-fit text-xs mb-2">
        {HELP_CATEGORIES[article.category]}
      </Badge>
      <h2 className="text-base font-semibold mb-4 leading-snug">{article.title}</h2>
      <div className="space-y-4 text-sm text-foreground overflow-y-auto flex-1 pr-1">
        {article.content.map((section, i) => (
          <div key={i}>
            {section.heading && (
              <p className="font-semibold text-foreground mb-1">{section.heading}</p>
            )}
            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
              {section.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Help tab ─────────────────────────────────────────────────────────────────
function HelpTab({ role }: { role?: "admin" | "engineer" }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<HelpArticle | null>(null);

  // Only show articles the user's role has access to
  const visibleArticles = useMemo(() =>
    HELP_ARTICLES.filter(a => !role || a.roles.includes(role)),
  [role]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return visibleArticles;
    return visibleArticles.filter(
      a =>
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q) ||
        a.content.some(
          s => s.body.toLowerCase().includes(q) || s.heading?.toLowerCase().includes(q),
        ),
    );
  }, [query, visibleArticles]);

  // Group by category when no search query
  const grouped = useMemo(() => {
    const map: Record<string, HelpArticle[]> = {};
    for (const a of filtered) {
      if (!map[a.category]) map[a.category] = [];
      map[a.category].push(a);
    }
    return map;
  }, [filtered]);

  if (selected) {
    return <ArticleView article={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-8 text-sm"
          placeholder="Search help articles…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid="input-help-search"
        />
      </div>

      <div className="overflow-y-auto flex-1 space-y-5 pr-1">
        {Object.entries(grouped).map(([cat, articles]) => (
          <div key={cat}>
            {!query && (
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {HELP_CATEGORIES[cat as HelpArticle["category"]]}
              </p>
            )}
            <div className="space-y-1">
              {articles.map(a => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className="w-full text-left px-3 py-2.5 rounded-md hover:bg-muted transition-colors group"
                  data-testid={`button-help-article-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{a.title}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 group-hover:text-foreground transition-colors" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{a.summary}</p>
                </button>
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No articles found for "{query}"
          </div>
        )}
      </div>
    </div>
  );
}

// ── Feedback tab ─────────────────────────────────────────────────────────────
type FeedbackType = "bug" | "feature" | "question";

const FEEDBACK_LABELS: Record<FeedbackType, string> = {
  bug: "Bug report",
  feature: "Feature request",
  question: "Question",
};

function FeedbackTab() {
  const { toast } = useToast();
  const [type, setType] = useState<FeedbackType>("bug");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit() {
    if (!description.trim()) {
      toast({ title: "Description required", description: "Please describe your issue or request.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequest("POST", "/api/feedback", {
        type,
        description: description.trim(),
        page: window.location.hash || "/",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to submit feedback");
      }
      setSubmitted(true);
    } catch (e: any) {
      toast({ title: "Could not submit", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-12">
        <CheckCircle2 className="w-10 h-10 text-green-500" />
        <p className="font-semibold text-foreground">Thanks — feedback received</p>
        <p className="text-sm text-muted-foreground">
          We'll review it and get back to you if needed.
        </p>
        <Button variant="outline" size="sm" onClick={() => { setSubmitted(false); setDescription(""); setType("bug"); }}>
          Send another
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      <p className="text-sm text-muted-foreground">
        Found a bug or have an idea? Let us know — it goes straight to the development team.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="feedback-type">Type</Label>
        <Select value={type} onValueChange={v => setType(v as FeedbackType)}>
          <SelectTrigger id="feedback-type" data-testid="select-feedback-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bug">Bug report</SelectItem>
            <SelectItem value="feature">Feature request</SelectItem>
            <SelectItem value="question">Question</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5 flex-1 flex flex-col">
        <Label htmlFor="feedback-description">Description</Label>
        <Textarea
          id="feedback-description"
          placeholder={
            type === "bug"
              ? "Describe what happened, what you expected, and how to reproduce it…"
              : type === "feature"
              ? "Describe the feature you'd like and why it would be useful…"
              : "What would you like to know?"
          }
          className="flex-1 min-h-[140px] resize-none text-sm"
          value={description}
          onChange={e => setDescription(e.target.value)}
          data-testid="textarea-feedback-description"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading || !description.trim()}
        className="w-full"
        data-testid="button-feedback-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Submit feedback
      </Button>
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────
export function HelpPanel({ open, onClose, role }: Props) {
  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0"
        data-testid="panel-help"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-base">Help &amp; Feedback</SheetTitle>
        </SheetHeader>
        <Tabs defaultValue="help" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 mt-4 mb-2 grid grid-cols-2 w-auto">
            <TabsTrigger value="help" data-testid="tab-help">Help</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          </TabsList>
          <TabsContent value="help" className="flex-1 overflow-hidden px-6 pb-6 mt-0">
            <HelpTab role={role} />
          </TabsContent>
          <TabsContent value="feedback" className="flex-1 px-6 pb-6 mt-0">
            <FeedbackTab />
          </TabsContent>
        </Tabs>
        <p className="px-6 pb-5 pt-2 text-xs text-muted-foreground text-center border-t">
          FireSafetyOps v{APP_VERSION}
        </p>
      </SheetContent>
    </Sheet>
  );
}
